import { useContext } from 'react';
import { Badge, BadgeProps, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { BookDatabase24Regular } from '@fluentui/react-icons';

import { useIsSafari } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { LiteratureSummary, OpenLiteratureButton } from '@modules/literatures';
import { isTDraft } from '@modules/items';
import { SignInContext } from '@modules/users';

import DocumentContext from '../../../contexts/DocumentContext.context';
import NodeBubbleMenuShell from '../../../components/NodeBubbleMenuShell';
import getDocumentProductId from '../../../helpers/getDocumentProductId';
import resolveLiteratureDecision from '../../../helpers/resolveLiteratureDecision.helper';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import { TResolvedLiteratureCitation } from '../../../types/reference.types';
import { LITERATURE_CITATION_NODE_NAME } from './constants';
import { classifyLiteratureCitation } from './LiteratureCitationNode';

const L = TERMINOLOGY.literature;

const HEADER_MAX_WIDTH = '360px';
const MENU_MAX_WIDTH = '480px';

const useStyles = makeStyles({
  headerColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    maxWidth: HEADER_MAX_WIDTH,
    rowGap: tokens.spacingVerticalXXS,
    marginRight: tokens.spacingHorizontalS,
  },
  statusBadge: {
    marginTop: tokens.spacingVerticalS,
    alignSelf: 'flex-start',
  },
});

type TStatusBadge = { color: BadgeProps['color']; label: string };

const resolveStatusBadge = (data: TResolvedLiteratureCitation | undefined): TStatusBadge | null => {
  switch (classifyLiteratureCitation(data)) {
    case 'excluded':
      return { color: 'danger', label: 'Excluded' };
    case 'missing':
      return { color: 'danger', label: `${L.upper} not found` };
    case 'pending':
      return { color: 'warning', label: 'Pending' };
    case 'unassessed':
      return { color: 'informative', label: 'Unassessed' };
    case 'not-required':
      return { color: 'informative', label: 'No assessment needed' };
    case 'archived':
      return { color: 'informative', label: 'Archived' };
    default:
      return null;
  }
};

const LiteratureCitationBubbleMenu = () => {
  const classes = useStyles();
  const isSafari = useIsSafari();
  const { doc, editor, version } = useContext(DocumentContext);
  const { organization } = useContext(SignInContext);

  const literatureId = useEditorActive(
    (e) => (e.getAttributes(LITERATURE_CITATION_NODE_NAME)?.literatureId as string | undefined) ?? ''
  );

  const entry = literatureId ? doc.resolvedLiteratureCitations?.[literatureId] : undefined;
  const isVersionView = !!version && !isTDraft(version);
  const frozenCitation = isVersionView ? version.literatureCitations.find((c) => c.itemId === literatureId) : undefined;
  const displayVersionId = frozenCitation?.versionId ?? entry?.latestVersionId;
  const displayContent = displayVersionId ? entry?.versions[displayVersionId] : undefined;

  // Version views read the regulatory snapshot (frozen axes) so the badge reflects status at freeze time, not live drift.
  const effectiveEntry =
    frozenCitation && entry
      ? {
          ...entry,
          decision: resolveLiteratureDecision(
            !!getDocumentProductId(doc, organization),
            frozenCitation.decision ?? null
          ),
          archived: frozenCitation.archived,
        }
      : entry;
  const statusBadge = resolveStatusBadge(effectiveEntry);

  const header = (() => {
    if (!literatureId) return null;
    if (!entry) {
      return (
        <div className={classes.headerColumn}>
          <span className="text-red-600">{L.upper} not found.</span>
        </div>
      );
    }
    return (
      <div className={classes.headerColumn}>
        <LiteratureSummary content={displayContent} />
        {statusBadge && (
          <Badge appearance="tint" color={statusBadge.color} className={classes.statusBadge}>
            {statusBadge.label}
          </Badge>
        )}
      </div>
    );
  })();

  const openLabel = `Open ${L.lower} in a new tab`;
  const openButton = literatureId ? <OpenLiteratureButton literatureId={literatureId} /> : null;

  return (
    <NodeBubbleMenuShell
      nodeName={LITERATURE_CITATION_NODE_NAME}
      editor={editor}
      isEdit={true /* show preview in view mode too */}
      maxWidth={MENU_MAX_WIDTH}
      leadingIcon={<BookDatabase24Regular className="text-brand-80" />}
      header={header}
    >
      {isSafari ? (
        openButton
      ) : (
        <Tooltip content={openLabel} relationship="label" withArrow>
          {openButton ?? <span />}
        </Tooltip>
      )}
    </NodeBubbleMenuShell>
  );
};

export default LiteratureCitationBubbleMenu;
